import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EChartsOption } from "echarts";
import { Link } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { Chart } from "../components/Chart.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { PageLayout } from "../components/PageLayout.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";

function toYmd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeLastDays(n: number): { from: string; to: string } {
  const end = new Date();
  const start = new Date(end.getTime() - n * 86_400_000);
  return { from: toYmd(start), to: toYmd(end) };
}

function fmtUsd(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n < 0.01 && n > 0 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

/** Registry USD-per-token (and web search): fixed decimals, no scientific notation (for display / read-only). */
function formatRegistryUnitPrice(n: number): string {
  if (!Number.isFinite(n)) return "";
  const s = n.toFixed(12);
  const trimmed = s.includes(".") ? s.replace(/\.?0+$/, "") : s;
  if (trimmed === "" || trimmed === "-" || trimmed === "-0") return "0";
  return trimmed;
}

type RegistryPriceField = "input_price" | "output_price" | "web_search_price";

function registryPriceDraftKey(modelId: string, field: RegistryPriceField): string {
  return `${modelId}\u001f${field}`;
}

function statusSelectClass(status: string): string {
  if (status === "active") return "pillSelect pillSelectOk";
  if (status === "disabled") return "pillSelect pillSelectBad";
  return "pillSelect pillSelectNeutral";
}

type AdminSummary = {
  total_calls: number;
  total_tokens: number;
  estimated_cost_usd: number;
  active_tenants: number;
  active_accounts: number;
};

type AdminBreakdownRow = {
  group_key: string;
  tenant_id?: string;
  calls: number;
  total_tokens: number;
  estimated_cost_usd: number;
};

const GROUP_OPTIONS = [
  { value: "", label: "Summary only" },
  { value: "tenant", label: "By tenant" },
  { value: "day", label: "By day" },
  { value: "model", label: "By model" },
  { value: "context_type", label: "By context type" }
] as const;

type HubMe = { user?: { role?: string | null } };

type AdminModel = {
  id: string;
  model_id: string;
  description: string | null;
  provider: string;
  api_profile_id: string;
  api_profile_label: string | null;
  status: string;
  tenant_visible: boolean;
  is_hub_default: boolean;
  /** ADR-0059: at most one model row has this true. */
  is_hub_default_web_model: boolean;
  /** ADR-0126: at most one model row has this true — platform default for background work. */
  is_hub_default_background_model: boolean;
  input_price: number;
  output_price: number;
  web_search_price: number;
  usage_30d_calls: number;
  usage_30d_tokens: number;
  usage_30d_cost: number;
};

type AdminAiProfile = {
  id: string;
  label: string;
  endpoint_url: string;
  response_parser: string;
  params: Record<string, unknown>;
  model_count: number;
};

type NewModelForm = {
  model_id: string;
  api_profile_id: string;
  status: string;
  tenant_visible: boolean;
  input_price: string;
  output_price: string;
  web_search_price: string;
};

function defaultNewModelForm(): NewModelForm {
  return {
    model_id: "",
    api_profile_id: "openai_responses",
    status: "active",
    tenant_visible: false,
    input_price: "0",
    output_price: "0",
    web_search_price: "0"
  };
}

type OpenAiApiModelRow = { id: string; object?: string; owned_by?: string; created?: number };

function fmtOpenAiCreated(sec?: number): string {
  if (sec == null || !Number.isFinite(sec)) return "—";
  return new Date(sec * 1000).toISOString().slice(0, 19).replace("T", " ");
}

const PROFILE_PARAM_TYPES = ["number", "integer", "string", "boolean", "json"] as const;
type ProfileParamType = (typeof PROFILE_PARAM_TYPES)[number];

type ProfileParamRow = {
  id: string;
  atlasKey: string;
  apiParam: string;
  type: ProfileParamType;
  defaultStr: string;
  minStr: string;
  maxStr: string;
  enumStr: string;
};

function newProfileParamRowId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyProfileParamRow(): ProfileParamRow {
  return {
    id: newProfileParamRowId(),
    atlasKey: "",
    apiParam: "",
    type: "json",
    defaultStr: "",
    minStr: "",
    maxStr: "",
    enumStr: ""
  };
}

function defaultValueToString(d: unknown): string {
  if (d === undefined) return "";
  try {
    return JSON.stringify(d);
  } catch {
    return String(d);
  }
}

function paramsRecordToRows(params: Record<string, unknown> | null | undefined): ProfileParamRow[] {
  if (!params || typeof params !== "object") return [];
  const keys = Object.keys(params).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  return keys.map((atlasKey) => {
    const v = params[atlasKey];
    const id = newProfileParamRowId();
    if (!v || typeof v !== "object" || Array.isArray(v)) {
      return { ...emptyProfileParamRow(), id, atlasKey };
    }
    const o = v as Record<string, unknown>;
    const apiParam = typeof o.api_param === "string" ? o.api_param : "";
    const tr = typeof o.type === "string" ? o.type : "json";
    const type = (PROFILE_PARAM_TYPES as readonly string[]).includes(tr) ? (tr as ProfileParamType) : "json";
    const minStr = typeof o.min === "number" && Number.isFinite(o.min) ? String(o.min) : "";
    const maxStr = typeof o.max === "number" && Number.isFinite(o.max) ? String(o.max) : "";
    const enumStr =
      Array.isArray(o.enum) ? JSON.stringify(o.enum) : o.enum !== undefined ? JSON.stringify(o.enum) : "";
    return {
      id,
      atlasKey,
      apiParam,
      type,
      defaultStr: o.default !== undefined ? defaultValueToString(o.default) : "",
      minStr,
      maxStr,
      enumStr
    };
  });
}

function buildParamsFromRows(rows: ProfileParamRow[]): { ok: true; params: Record<string, unknown> } | { ok: false; message: string } {
  const params: Record<string, unknown> = {};
  const seen = new Set<string>();
  for (const r of rows) {
    const key = r.atlasKey.trim();
    if (!key) continue;
    if (seen.has(key)) return { ok: false, message: `Duplicate Atlas name: ${key}` };
    seen.add(key);
    const api = r.apiParam.trim();
    if (!api) return { ok: false, message: `API param is required for row “${key}”.` };
    const def: Record<string, unknown> = { api_param: api, type: r.type };
    if (r.minStr.trim()) {
      const n = Number(r.minStr.trim());
      if (!Number.isFinite(n)) return { ok: false, message: `Invalid min for “${key}”.` };
      def.min = n;
    }
    if (r.maxStr.trim()) {
      const n = Number(r.maxStr.trim());
      if (!Number.isFinite(n)) return { ok: false, message: `Invalid max for “${key}”.` };
      def.max = n;
    }
    if (r.defaultStr.trim()) {
      const t = r.defaultStr.trim();
      try {
        def.default = JSON.parse(t);
      } catch {
        def.default = t;
      }
    }
    if (r.enumStr.trim()) {
      try {
        const arr = JSON.parse(r.enumStr.trim()) as unknown;
        if (!Array.isArray(arr)) return { ok: false, message: `Enum for “${key}” must be a JSON array.` };
        def.enum = arr;
      } catch {
        return { ok: false, message: `Invalid enum JSON for “${key}”.` };
      }
    }
    params[key] = def;
  }
  return { ok: true, params };
}

type ProfileParamsTableProps = {
  rows: ProfileParamRow[];
  onChange: (rows: ProfileParamRow[]) => void;
  disabled?: boolean;
};

function ProfileParamsTable({ rows, onChange, disabled }: ProfileParamsTableProps) {
  const updateRow = (index: number, patch: Partial<ProfileParamRow>) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };
  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };
  const addRow = () => {
    onChange([...rows, emptyProfileParamRow()]);
  };

  return (
    <div>
      <div className="tableWrap" style={{ overflowX: "auto", marginBottom: 8 }}>
        <table className="table tableCompact" style={{ fontSize: 11, minWidth: 720 }}>
          <thead>
            <tr>
              <th>Atlas name</th>
              <th>API param</th>
              <th>Type</th>
              <th>Default</th>
              <th>Min</th>
              <th>Max</th>
              <th>Enum (JSON array)</th>
              <th style={{ width: 56 }} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted" style={{ padding: 10 }}>
                  No parameters. Use “Add param” to add a row.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.id}>
                  <td style={{ minWidth: 120 }}>
                    <input
                      className="input mono"
                      style={{ fontSize: 11, padding: "2px 6px" }}
                      value={r.atlasKey}
                      disabled={disabled}
                      spellCheck={false}
                      onChange={(e) => updateRow(i, { atlasKey: e.target.value })}
                    />
                  </td>
                  <td style={{ minWidth: 120 }}>
                    <input
                      className="input mono"
                      style={{ fontSize: 11, padding: "2px 6px" }}
                      value={r.apiParam}
                      disabled={disabled}
                      spellCheck={false}
                      onChange={(e) => updateRow(i, { apiParam: e.target.value })}
                    />
                  </td>
                  <td style={{ minWidth: 88 }}>
                    <select
                      className="select"
                      style={{ fontSize: 11, padding: "2px 4px" }}
                      value={r.type}
                      disabled={disabled}
                      onChange={(e) => updateRow(i, { type: e.target.value as ProfileParamType })}
                    >
                      {PROFILE_PARAM_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ minWidth: 100 }}>
                    <input
                      className="input mono"
                      style={{ fontSize: 11, padding: "2px 6px" }}
                      value={r.defaultStr}
                      disabled={disabled}
                      spellCheck={false}
                      placeholder="JSON or text"
                      title='JSON literal (e.g. 1, true, "x") or plain text'
                      onChange={(e) => updateRow(i, { defaultStr: e.target.value })}
                    />
                  </td>
                  <td style={{ minWidth: 64 }}>
                    <input
                      className="input mono"
                      style={{ fontSize: 11, padding: "2px 6px" }}
                      value={r.minStr}
                      disabled={disabled}
                      spellCheck={false}
                      onChange={(e) => updateRow(i, { minStr: e.target.value })}
                    />
                  </td>
                  <td style={{ minWidth: 64 }}>
                    <input
                      className="input mono"
                      style={{ fontSize: 11, padding: "2px 6px" }}
                      value={r.maxStr}
                      disabled={disabled}
                      spellCheck={false}
                      onChange={(e) => updateRow(i, { maxStr: e.target.value })}
                    />
                  </td>
                  <td style={{ minWidth: 140 }}>
                    <input
                      className="input mono"
                      style={{ fontSize: 11, padding: "2px 6px" }}
                      value={r.enumStr}
                      disabled={disabled}
                      spellCheck={false}
                      placeholder='["a","b"]'
                      onChange={(e) => updateRow(i, { enumStr: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btnSm"
                      style={{ fontSize: 11, padding: "2px 8px" }}
                      disabled={disabled}
                      onClick={() => removeRow(i)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button type="button" className="btn btnSm" disabled={disabled} onClick={addRow}>
        + Add param
      </button>
    </div>
  );
}

export function AdminAiAdmin() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [tab, setTab] = useState<"usage" | "models" | "profiles" | "openai_models">("usage");

  const [from, setFrom] = useState(rangeLastDays(30).from);
  const [to, setTo] = useState(rangeLastDays(30).to);
  const [groupBy, setGroupBy] = useState<(typeof GROUP_OPTIONS)[number]["value"]>("tenant");
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [breakdown, setBreakdown] = useState<AdminBreakdownRow[]>([]);

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      const q = new URLSearchParams({ from, to });
      if (groupBy !== "") q.set("group_by", groupBy);
      const data = await apiFetch<{ summary: AdminSummary; breakdown: AdminBreakdownRow[] }>(`/api/admin/ai/usage?${q.toString()}`);
      setSummary(data.summary);
      setBreakdown(data.breakdown);
    } catch (e) {
      setUsageError(e instanceof Error ? e.message : String(e));
      setSummary(null);
      setBreakdown([]);
    } finally {
      setUsageLoading(false);
    }
  }, [from, to, groupBy]);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  const chartOption = useMemo((): EChartsOption | null => {
    if (!groupBy || breakdown.length === 0) return null;
    const rows = [...breakdown].sort((a, b) => b.calls - a.calls).slice(0, 40);
    const isTenant = groupBy === "tenant";
    return {
      grid: { left: isTenant ? 140 : 48, right: 16, top: 24, bottom: 48 },
      tooltip: { trigger: "axis" },
      xAxis: isTenant
        ? { type: "value", name: "Calls" }
        : { type: "category", data: rows.map((r) => r.group_key), axisLabel: { rotate: rows.length > 12 ? 45 : 0 } },
      yAxis: isTenant
        ? { type: "category", data: rows.map((r) => r.group_key), inverse: true }
        : { type: "value", name: "Calls" },
      series: [{ type: "bar", name: "Calls", data: rows.map((r) => r.calls), itemStyle: { color: "#6366f1" } }]
    };
  }, [breakdown, groupBy]);

  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [models, setModels] = useState<AdminModel[]>([]);
  const [hubRole, setHubRole] = useState<string | null>(null);
  const [savingModelId, setSavingModelId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newModel, setNewModel] = useState<NewModelForm>(() => defaultNewModelForm());
  const [deleteTarget, setDeleteTarget] = useState<AdminModel | null>(null);
  /** In-table price edits: raw string while typing so values like `0.0000001` and `0.0` are not collapsed by Number() on each keystroke. */
  const [registryPriceDrafts, setRegistryPriceDrafts] = useState<Record<string, string>>({});
  const canMutate = hubRole === "hub_admin";

  const [profiles, setProfiles] = useState<AdminAiProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState<string | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [profileEditLabel, setProfileEditLabel] = useState("");
  const [profileEditEndpoint, setProfileEditEndpoint] = useState("");
  const [profileEditParser, setProfileEditParser] = useState("openai_responses");
  const [profileEditParamRows, setProfileEditParamRows] = useState<ProfileParamRow[]>([]);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileAddOpen, setProfileAddOpen] = useState(false);
  const [newProfileId, setNewProfileId] = useState("");
  const [newProfileLabel, setNewProfileLabel] = useState("");
  const [newProfileEndpoint, setNewProfileEndpoint] = useState("https://api.openai.com/v1/responses");
  const [newProfileParser, setNewProfileParser] = useState("openai_responses");
  const [newProfileParamRows, setNewProfileParamRows] = useState<ProfileParamRow[]>([]);
  const [profileDeleteTarget, setProfileDeleteTarget] = useState<AdminAiProfile | null>(null);

  const loadProfiles = useCallback(async () => {
    setProfilesLoading(true);
    setProfilesError(null);
    try {
      const data = await apiFetch<{ profiles: AdminAiProfile[] }>("/api/admin/ai/profiles");
      setProfiles(data.profiles ?? []);
    } catch (e) {
      setProfilesError(e instanceof Error ? e.message : String(e));
      setProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hubRole !== "hub_admin") return;
    void loadProfiles();
  }, [hubRole, loadProfiles]);

  const [openaiModelsLoading, setOpenaiModelsLoading] = useState(false);
  const [openaiModelsError, setOpenaiModelsError] = useState<string | null>(null);
  const [openaiModels, setOpenaiModels] = useState<OpenAiApiModelRow[]>([]);
  const [openaiFetchedAt, setOpenaiFetchedAt] = useState<string | null>(null);
  const openaiListBootstrapped = useRef(false);

  const loadOpenaiModels = useCallback(async () => {
    setOpenaiModelsLoading(true);
    setOpenaiModelsError(null);
    try {
      const data = await apiFetch<{ models: OpenAiApiModelRow[]; fetched_at?: string }>("/api/admin/ai/openai-models");
      setOpenaiModels(data.models ?? []);
      setOpenaiFetchedAt(data.fetched_at ?? null);
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      setOpenaiModelsError(msg);
      setOpenaiModels([]);
      setOpenaiFetchedAt(null);
    } finally {
      setOpenaiModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== "openai_models") return;
    if (openaiListBootstrapped.current) return;
    openaiListBootstrapped.current = true;
    void loadOpenaiModels();
  }, [tab, loadOpenaiModels]);

  const loadModels = useCallback(async () => {
    setModelsLoading(true);
    setModelsError(null);
    try {
      const me = await apiFetch<HubMe>("/api/auth/me");
      setHubRole(me.user?.role ?? null);
      const data = await apiFetch<{ models: AdminModel[] }>("/api/admin/ai/models");
      setModels(data.models);
    } catch (e) {
      setModelsError(e instanceof Error ? e.message : String(e));
      setModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadModels();
  }, [loadModels]);

  const modelsSorted = useMemo(
    () => [...models].sort((a, b) => a.model_id.localeCompare(b.model_id, undefined, { sensitivity: "base" })),
    [models]
  );

  function updateLocalModel(modelId: string, patch: Partial<AdminModel>) {
    setModels((prev) => prev.map((m) => (m.model_id === modelId ? { ...m, ...patch } : m)));
  }

  function commitRegistryPriceOnBlur(modelId: string, field: RegistryPriceField, raw: string, previous: number) {
    const k = registryPriceDraftKey(modelId, field);
    setRegistryPriceDrafts((prev) => {
      if (!(k in prev)) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
    const t = raw.trim();
    if (t === "") {
      toast.push({ kind: "error", message: "Price cannot be empty." });
      return;
    }
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) {
      toast.push({ kind: "error", message: "Enter a valid non-negative number." });
      return;
    }
    if (n === previous) return;
    updateLocalModel(modelId, { [field]: n } as Partial<AdminModel>);
    void patchModel(modelId, { [field]: n });
  }

  async function patchModel(modelId: string, patch: Record<string, unknown>) {
    if (!canMutate) return;
    setSavingModelId(modelId);
    try {
      await apiFetch(`/api/admin/ai/models/${encodeURIComponent(modelId)}`, { method: "PATCH", body: patch });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      await loadModels();
    } finally {
      setSavingModelId((prev) => (prev === modelId ? null : prev));
    }
  }

  async function setDefault(modelId: string) {
    if (!canMutate) return;
    setSavingModelId(modelId);
    try {
      await apiFetch(`/api/admin/ai/models/${encodeURIComponent(modelId)}/set-default`, { method: "PATCH", body: {} });
      await loadModels();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSavingModelId((prev) => (prev === modelId ? null : prev));
    }
  }

  async function setDefaultWebModel(modelId: string) {
    if (!canMutate) return;
    setSavingModelId(modelId);
    try {
      await apiFetch(`/api/admin/ai/models/${encodeURIComponent(modelId)}/set-default-web`, { method: "PATCH", body: {} });
      await loadModels();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSavingModelId((prev) => (prev === modelId ? null : prev));
    }
  }

  async function setDefaultBackgroundModel(modelId: string) {
    if (!canMutate) return;
    setSavingModelId(modelId);
    try {
      await apiFetch(`/api/admin/ai/models/${encodeURIComponent(modelId)}/set-default-background`, { method: "PATCH", body: {} });
      await loadModels();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSavingModelId((prev) => (prev === modelId ? null : prev));
    }
  }

  async function deleteModel(modelId: string): Promise<boolean> {
    if (!canMutate) return false;
    setSavingModelId(modelId);
    try {
      await apiFetch(`/api/admin/ai/models/${encodeURIComponent(modelId)}`, { method: "DELETE" });
      setModels((prev) => prev.filter((m) => m.model_id !== modelId));
      return true;
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 409 && e.code === "model_has_usage") {
        toast.push({ kind: "error", message: "This model has usage history. Set status to disabled instead." });
        return false;
      }
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      return false;
    } finally {
      setSavingModelId((prev) => (prev === modelId ? null : prev));
    }
  }

  async function confirmDeleteModel() {
    if (!deleteTarget) return;
    const ok = await deleteModel(deleteTarget.model_id);
    if (ok) setDeleteTarget(null);
  }

  async function createModel() {
    if (!canMutate) return;
    const modelId = newModel.model_id.trim();
    if (!modelId) {
      toast.push({ kind: "error", message: "Model id is required." });
      return;
    }
    const input = Number(newModel.input_price);
    const output = Number(newModel.output_price);
    const search = Number(newModel.web_search_price);
    if (![input, output, search].every((v) => Number.isFinite(v) && v >= 0)) {
      toast.push({ kind: "error", message: "Prices must be valid non-negative numbers." });
      return;
    }
    setSavingModelId(modelId);
    try {
      await apiFetch("/api/admin/ai/models", {
        method: "POST",
        body: {
          model_id: modelId,
          api_profile_id: newModel.api_profile_id.trim() || "openai_responses",
          provider: "openai",
          status: newModel.status,
          tenant_visible: newModel.tenant_visible,
          input_price: input,
          output_price: output,
          web_search_price: search
        }
      });
      setAdding(false);
      setNewModel(defaultNewModelForm());
      await loadModels();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "duplicate_model_id") {
        toast.push({ kind: "error", message: "A model with this id already exists." });
        return;
      }
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSavingModelId((prev) => (prev === modelId ? null : prev));
    }
  }

  function openProfileEditor(p: AdminAiProfile) {
    setSelectedProfileId(p.id);
    setProfileEditLabel(p.label);
    setProfileEditEndpoint(p.endpoint_url);
    setProfileEditParser(p.response_parser);
    setProfileEditParamRows(paramsRecordToRows(p.params));
  }

  async function saveProfileEdit() {
    if (!selectedProfileId) return;
    const built = buildParamsFromRows(profileEditParamRows);
    if (!built.ok) {
      toast.push({ kind: "error", message: built.message });
      return;
    }
    const params = built.params;
    setProfileSaving(true);
    try {
      const saved = await apiFetch<{ params: Record<string, unknown> }>(
        `/api/admin/ai/profiles/${encodeURIComponent(selectedProfileId)}`,
        {
          method: "PATCH",
          body: {
            label: profileEditLabel,
            endpoint_url: profileEditEndpoint,
            response_parser: profileEditParser,
            params
          }
        }
      );
      toast.push({ kind: "success", message: "Profile saved." });
      setProfileEditParamRows(paramsRecordToRows(saved.params));
      await loadProfiles();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setProfileSaving(false);
    }
  }

  async function submitNewProfile() {
    const id = newProfileId.trim();
    if (!id) {
      toast.push({ kind: "error", message: "Profile id is required." });
      return;
    }
    const built = buildParamsFromRows(newProfileParamRows);
    if (!built.ok) {
      toast.push({ kind: "error", message: built.message });
      return;
    }
    const params = built.params;
    setProfileSaving(true);
    try {
      await apiFetch("/api/admin/ai/profiles", {
        method: "POST",
        body: {
          id,
          label: newProfileLabel.trim() || id,
          endpoint_url: newProfileEndpoint.trim(),
          response_parser: newProfileParser,
          params
        }
      });
      toast.push({ kind: "success", message: "Profile created." });
      setProfileAddOpen(false);
      setNewProfileId("");
      setNewProfileLabel("");
      setNewProfileEndpoint("https://api.openai.com/v1/responses");
      setNewProfileParser("openai_responses");
      setNewProfileParamRows([]);
      await loadProfiles();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "duplicate_profile_id") {
        toast.push({ kind: "error", message: "A profile with this id already exists." });
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    } finally {
      setProfileSaving(false);
    }
  }

  const [recalcBusy, setRecalcBusy] = useState(false);

  async function recalculateStoredCosts() {
    if (!canMutate || recalcBusy) return;
    setRecalcBusy(true);
    try {
      const dry = await apiFetch<{
        result: {
          considered: number;
          resolved: number;
          unresolved: number;
          changed: number;
          total_cost_before: number;
          total_cost_after: number;
        };
      }>("/api/admin/ai/usage/recalculate-costs", { method: "POST", body: { dry_run: true } });
      const r = dry.result;
      if (r.changed === 0) {
        toast.push({
          kind: "success",
          message: `No changes — all ${r.resolved.toLocaleString()} resolved rows already match current pricing.`
        });
        return;
      }
      const delta = r.total_cost_after - r.total_cost_before;
      const sign = delta >= 0 ? "+" : "−";
      const ok = await confirm({
        title: "Recalculate stored AI costs?",
        detail: [
          `${r.changed.toLocaleString()} of ${r.considered.toLocaleString()} rows will change.`,
          `Total: ${fmtUsd(r.total_cost_before)} → ${fmtUsd(r.total_cost_after)} (${sign}${fmtUsd(Math.abs(delta))}).`,
          r.unresolved > 0
            ? `${r.unresolved.toLocaleString()} rows will be skipped (model not in registry).`
            : null
        ]
          .filter(Boolean)
          .join(" "),
        confirmLabel: "Recalculate"
      });
      if (!ok) return;
      const applied = await apiFetch<{
        result: { changed: number; total_cost_before: number; total_cost_after: number };
      }>("/api/admin/ai/usage/recalculate-costs", { method: "POST", body: { dry_run: false } });
      const a = applied.result;
      toast.push({
        kind: "success",
        message: `Recalculated ${a.changed.toLocaleString()} rows. Total: ${fmtUsd(a.total_cost_before)} → ${fmtUsd(a.total_cost_after)}.`
      });
      await Promise.all([loadUsage(), loadModels()]);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRecalcBusy(false);
    }
  }

  async function confirmDeleteProfile() {
    if (!profileDeleteTarget) return;
    const tid = profileDeleteTarget.id;
    setProfileSaving(true);
    try {
      await apiFetch(`/api/admin/ai/profiles/${encodeURIComponent(tid)}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Profile deleted." });
      setProfileDeleteTarget(null);
      setSelectedProfileId((cur) => (cur === tid ? null : cur));
      await loadProfiles();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "profile_has_models") {
        toast.push({ kind: "error", message: "Reassign models before deleting this profile." });
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    } finally {
      setProfileSaving(false);
    }
  }

  return (
    <PageLayout title="AI Administration">
      <div className="tabs">
        <button type="button" className={`tab${tab === "usage" ? " tabActive" : ""}`} onClick={() => setTab("usage")}>
          Usage
        </button>
        <button type="button" className={`tab${tab === "models" ? " tabActive" : ""}`} onClick={() => setTab("models")}>
          Models
        </button>
        {hubRole === "hub_admin" ? (
          <button
            type="button"
            className={`tab${tab === "profiles" ? " tabActive" : ""}`}
            onClick={() => setTab("profiles")}
          >
            API profiles
          </button>
        ) : null}
        <button
          type="button"
          className={`tab${tab === "openai_models" ? " tabActive" : ""}`}
          onClick={() => setTab("openai_models")}
        >
          OpenAI models (test)
        </button>
      </div>

      {tab === "usage" ? (
        <div className="tabPanel">
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
            {canMutate ? (
              <button
                type="button"
                className="btn btnSm"
                disabled={recalcBusy}
                title="Re-apply current registry pricing to all historical ai_usage_log rows"
                onClick={() => void recalculateStoredCosts()}
              >
                {recalcBusy ? "Recalculating…" : "Recalculate stored costs"}
              </button>
            ) : null}
            <Link to="/admin/tenants" className="btn btnSm">
              Tenants
            </Link>
          </div>

          <div className="card" style={{ padding: 12, marginBottom: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="label">From</span>
                <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="label">To</span>
                <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="label">Breakdown</span>
                <select
                  className="select"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as (typeof GROUP_OPTIONS)[number]["value"])}
                >
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o.value || "none"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {usageError ? <ErrorState detail={usageError} /> : null}
          {usageLoading ? <LoadingState /> : null}

          {!usageLoading && !usageError && summary ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 12,
                  marginBottom: 12
                }}
              >
                {(
                  [
                    { label: "Total calls", value: summary.total_calls.toLocaleString() },
                    { label: "Total tokens", value: summary.total_tokens.toLocaleString() },
                    { label: "Est. cost", value: fmtUsd(summary.estimated_cost_usd) },
                    { label: "Active tenants", value: summary.active_tenants.toLocaleString() },
                    { label: "Active accounts", value: summary.active_accounts.toLocaleString() }
                  ] as const
                ).map(({ label, value }) => (
                  <div key={label} className="card" style={{ padding: 12 }}>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
                  </div>
                ))}
              </div>

              {chartOption ? (
                <div className="card" style={{ padding: 12, marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Breakdown</div>
                  <Chart option={chartOption} height={320} />
                </div>
              ) : null}

              {groupBy === "tenant" && breakdown.length > 0 ? (
                <div className="card" style={{ padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Tenants (top {Math.min(breakdown.length, 40)})</div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ width: "100%", fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>Tenant</th>
                          <th>Calls</th>
                          <th>Tokens</th>
                          <th>Est. USD</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {[...breakdown]
                          .sort((a, b) => b.calls - a.calls)
                          .map((row) => (
                            <tr key={row.tenant_id ?? row.group_key}>
                              <td>{row.group_key}</td>
                              <td>{row.calls.toLocaleString()}</td>
                              <td>{row.total_tokens.toLocaleString()}</td>
                              <td>{fmtUsd(row.estimated_cost_usd)}</td>
                              <td>
                                {row.tenant_id ? (
                                  <Link className="btn btnSm" to={`/admin/tenants/${row.tenant_id}/ai-usage`}>
                                    Details
                                  </Link>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {groupBy && groupBy !== "tenant" && breakdown.length > 0 ? (
                <div className="card" style={{ padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Rows</div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ width: "100%", fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>Key</th>
                          <th>Calls</th>
                          <th>Tokens</th>
                          <th>Est. USD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...breakdown]
                          .sort((a, b) => b.calls - a.calls)
                          .map((row) => (
                            <tr key={row.group_key}>
                              <td className="mono">{row.group_key}</td>
                              <td>{row.calls.toLocaleString()}</td>
                              <td>{row.total_tokens.toLocaleString()}</td>
                              <td>{fmtUsd(row.estimated_cost_usd)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {tab === "models" ? (
        <div className="tabPanel">
          {!canMutate && hubRole === "hub_operator" ? (
            <div className="card muted" style={{ marginBottom: 10 }}>
              You have read-only access. Hub administrators can edit models.
            </div>
          ) : null}

          {canMutate ? (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <a
                href="#"
                className="linkBtn menuLinkAction"
                onClick={(e) => {
                  e.preventDefault();
                  setNewModel(defaultNewModelForm());
                  setAdding(true);
                }}
              >
                + Add model
              </a>
            </div>
          ) : null}

          {modelsLoading ? <LoadingState label="Loading models..." /> : null}
          {!modelsLoading && modelsError ? <ErrorState detail={modelsError} /> : null}

          {!modelsLoading && !modelsError ? (
            <div className="card tableWrap">
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>API profile</th>
                    <th>Status</th>
                    <th>Default</th>
                    <th title="Default web-capable model (ADR-0059): used for web-search calls when the selected model can't search">Default web</th>
                    <th title="Default model for background work (knowledge extraction, session summaries) when a tenant has no override">Background default</th>
                    <th>Visible</th>
                    <th>Input</th>
                    <th>Output</th>
                    <th>Web search</th>
                    <th>30d calls</th>
                    <th>30d cost</th>
                    {canMutate ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {modelsSorted.map((m) => (
                    <tr key={m.id}>
                      <td style={{ minWidth: 160 }}>
                        <span className="mono" style={{ fontSize: 11 }}>
                          {m.model_id}
                        </span>
                      </td>
                      <td style={{ minWidth: 140 }}>
                        {canMutate && profiles.length > 0 ? (
                          <select
                            className="select"
                            style={{ maxWidth: 200, fontSize: 11 }}
                            value={m.api_profile_id}
                            disabled={savingModelId === m.model_id}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateLocalModel(m.model_id, { api_profile_id: v });
                              void patchModel(m.model_id, { api_profile_id: v });
                            }}
                          >
                            {profiles.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="muted" style={{ fontSize: 11 }}>
                            {m.api_profile_label ?? m.api_profile_id}
                          </span>
                        )}
                      </td>
                      <td>
                        {canMutate ? (
                          <select
                            className={statusSelectClass(m.status)}
                            value={m.status}
                            disabled={savingModelId === m.model_id}
                            onChange={(e) => {
                              updateLocalModel(m.model_id, { status: e.target.value });
                              void patchModel(m.model_id, { status: e.target.value });
                            }}
                          >
                            <option value="active">active</option>
                            <option value="pending">pending</option>
                            <option value="deprecated">deprecated</option>
                            <option value="disabled">disabled</option>
                          </select>
                        ) : (
                          m.status
                        )}
                      </td>
                      <td>
                        <input
                          type="radio"
                          name="hub-default-model"
                          checked={m.is_hub_default}
                          disabled={!canMutate || savingModelId === m.model_id || m.status !== "active"}
                          onChange={() => void setDefault(m.model_id)}
                        />
                      </td>
                      <td>
                        <input
                          type="radio"
                          name="hub-default-web-model"
                          checked={m.is_hub_default_web_model}
                          disabled={
                            !canMutate ||
                            savingModelId === m.model_id ||
                            m.status !== "active" ||
                            !(m.web_search_price > 0)
                          }
                          onChange={() => void setDefaultWebModel(m.model_id)}
                          title={
                            m.web_search_price > 0
                              ? undefined
                              : "Not web-capable — set a web_search_price > 0 to enable"
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="radio"
                          name="hub-default-background-model"
                          checked={m.is_hub_default_background_model}
                          disabled={!canMutate || savingModelId === m.model_id || m.status !== "active"}
                          onChange={() => void setDefaultBackgroundModel(m.model_id)}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={m.tenant_visible}
                          disabled={!canMutate || savingModelId === m.model_id}
                          onChange={(e) => {
                            updateLocalModel(m.model_id, { tenant_visible: e.target.checked });
                            void patchModel(m.model_id, { tenant_visible: e.target.checked });
                          }}
                        />
                      </td>
                      <td>
                        {canMutate ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            spellCheck={false}
                            className="input mono"
                            value={registryPriceDrafts[registryPriceDraftKey(m.model_id, "input_price")] ?? formatRegistryUnitPrice(m.input_price)}
                            disabled={savingModelId === m.model_id}
                            onChange={(e) => {
                              const k = registryPriceDraftKey(m.model_id, "input_price");
                              setRegistryPriceDrafts((prev) => ({ ...prev, [k]: e.target.value }));
                            }}
                            onBlur={(e) =>
                              commitRegistryPriceOnBlur(m.model_id, "input_price", e.target.value, m.input_price)
                            }
                            style={{ minWidth: 128, width: 128, fontSize: 12, padding: "2px 4px" }}
                          />
                        ) : (
                          <span className="mono" style={{ fontSize: 12 }}>
                            {formatRegistryUnitPrice(m.input_price)}
                          </span>
                        )}
                      </td>
                      <td>
                        {canMutate ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            spellCheck={false}
                            className="input mono"
                            value={registryPriceDrafts[registryPriceDraftKey(m.model_id, "output_price")] ?? formatRegistryUnitPrice(m.output_price)}
                            disabled={savingModelId === m.model_id}
                            onChange={(e) => {
                              const k = registryPriceDraftKey(m.model_id, "output_price");
                              setRegistryPriceDrafts((prev) => ({ ...prev, [k]: e.target.value }));
                            }}
                            onBlur={(e) =>
                              commitRegistryPriceOnBlur(m.model_id, "output_price", e.target.value, m.output_price)
                            }
                            style={{ minWidth: 128, width: 128, fontSize: 12, padding: "2px 4px" }}
                          />
                        ) : (
                          <span className="mono" style={{ fontSize: 12 }}>
                            {formatRegistryUnitPrice(m.output_price)}
                          </span>
                        )}
                      </td>
                      <td>
                        {canMutate ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            spellCheck={false}
                            className="input mono"
                            value={registryPriceDrafts[registryPriceDraftKey(m.model_id, "web_search_price")] ?? formatRegistryUnitPrice(m.web_search_price)}
                            disabled={savingModelId === m.model_id}
                            onChange={(e) => {
                              const k = registryPriceDraftKey(m.model_id, "web_search_price");
                              setRegistryPriceDrafts((prev) => ({ ...prev, [k]: e.target.value }));
                            }}
                            onBlur={(e) =>
                              commitRegistryPriceOnBlur(m.model_id, "web_search_price", e.target.value, m.web_search_price)
                            }
                            style={{ minWidth: 128, width: 128, fontSize: 12, padding: "2px 4px" }}
                          />
                        ) : (
                          <span className="mono" style={{ fontSize: 12 }}>
                            {formatRegistryUnitPrice(m.web_search_price)}
                          </span>
                        )}
                      </td>
                      <td>{m.usage_30d_calls.toLocaleString()}</td>
                      <td>{fmtUsd(m.usage_30d_cost)}</td>
                      {canMutate ? (
                        <td style={{ whiteSpace: "nowrap" }}>
                          <a
                            href="#"
                            className="linkBtn menuLinkAction linkBtnDanger"
                            aria-disabled={
                              savingModelId === m.model_id ||
                              m.is_hub_default ||
                              m.is_hub_default_web_model ||
                              m.is_hub_default_background_model
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              if (
                                savingModelId === m.model_id ||
                                m.is_hub_default ||
                                m.is_hub_default_web_model ||
                                m.is_hub_default_background_model
                              )
                                return;
                              setDeleteTarget(m);
                            }}
                            title={
                              m.is_hub_default
                                ? "Cannot delete the current hub default model"
                                : m.is_hub_default_web_model
                                  ? "Cannot delete the current default web model"
                                  : m.is_hub_default_background_model
                                    ? "Cannot delete the current default background model"
                                    : undefined
                            }
                            style={
                              savingModelId === m.model_id ||
                              m.is_hub_default ||
                              m.is_hub_default_web_model ||
                              m.is_hub_default_background_model
                                ? { opacity: 0.45, pointerEvents: "none", textDecoration: "none" }
                                : undefined
                            }
                          >
                            Delete
                          </a>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                  {models.length === 0 ? (
                    <tr>
                      <td colSpan={canMutate ? 13 : 12} className="muted" style={{ padding: 12 }}>
                        No models in registry.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}

          {adding && canMutate ? (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 80,
                padding: 16
              }}
              role="presentation"
              onMouseDown={() => setAdding(false)}
            >
              <div
                className="card"
                style={{ maxWidth: 420, width: "100%", display: "grid", gap: 12, maxHeight: "90vh", overflowY: "auto" }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-model-title"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div id="add-model-title" style={{ fontWeight: 700, fontSize: 15 }}>
                  Add model
                </div>
                <label className="field">
                  <span className="label">Model id</span>
                  <input
                    className="input"
                    placeholder="e.g. gpt-5.1"
                    value={newModel.model_id}
                    onChange={(e) => setNewModel((prev) => ({ ...prev, model_id: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="label">API profile</span>
                  <select
                    className="select"
                    value={newModel.api_profile_id}
                    onChange={(e) => setNewModel((prev) => ({ ...prev, api_profile_id: e.target.value }))}
                  >
                    {(profiles.length > 0 ? profiles : [{ id: "openai_responses", label: "OpenAI Responses" }]).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Status</span>
                  <select
                    className="select"
                    value={newModel.status}
                    onChange={(e) => setNewModel((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="active">active</option>
                    <option value="pending">pending</option>
                    <option value="deprecated">deprecated</option>
                    <option value="disabled">disabled</option>
                  </select>
                </label>
                <label className="field" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={newModel.tenant_visible}
                    onChange={(e) => setNewModel((prev) => ({ ...prev, tenant_visible: e.target.checked }))}
                  />
                  <span>Tenant visible</span>
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <label className="field">
                    <span className="label">Input price (USD / token)</span>
                    <input
                      type="number"
                      step="any"
                      className="input"
                      value={newModel.input_price}
                      onChange={(e) => setNewModel((prev) => ({ ...prev, input_price: e.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span className="label">Output price (USD / token)</span>
                    <input
                      type="number"
                      step="any"
                      className="input"
                      value={newModel.output_price}
                      onChange={(e) => setNewModel((prev) => ({ ...prev, output_price: e.target.value }))}
                    />
                  </label>
                </div>
                <label className="field">
                  <span className="label">Web search price (USD / call)</span>
                  <input
                    type="number"
                    step="any"
                    className="input"
                    value={newModel.web_search_price}
                    onChange={(e) => setNewModel((prev) => ({ ...prev, web_search_price: e.target.value }))}
                  />
                </label>
                <div className="actions" style={{ justifyContent: "flex-end" }}>
                  <a
                    href="#"
                    className="linkBtn menuLinkAction"
                    onClick={(e) => {
                      e.preventDefault();
                      setAdding(false);
                    }}
                  >
                    Cancel
                  </a>
                  <a
                    href="#"
                    className="linkBtn menuLinkAction"
                    onClick={(e) => {
                      e.preventDefault();
                      void createModel();
                    }}
                  >
                    Create
                  </a>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "profiles" && hubRole === "hub_admin" ? (
        <div className="tabPanel">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <a
              href="#"
              className="linkBtn menuLinkAction"
              onClick={(e) => {
                e.preventDefault();
                setNewProfileParamRows([]);
                setProfileAddOpen(true);
              }}
            >
              + Add profile
            </a>
          </div>
          {profilesLoading ? <LoadingState label="Loading profiles…" /> : null}
          {!profilesLoading && profilesError ? <ErrorState detail={profilesError} /> : null}
          {!profilesLoading && !profilesError ? (
            <div className="card tableWrap" style={{ marginBottom: 12 }}>
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Endpoint</th>
                    <th>Parser</th>
                    <th>Models</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <tr
                      key={p.id}
                      style={{
                        cursor: "pointer",
                        background: selectedProfileId === p.id ? "var(--panel-2, rgba(0,0,0,0.06))" : undefined
                      }}
                      onClick={() => openProfileEditor(p)}
                    >
                      <td>
                        <div style={{ fontWeight: 600 }}>{p.label}</div>
                        <div className="mono muted" style={{ fontSize: 11 }}>
                          {p.id}
                        </div>
                      </td>
                      <td className="mono" style={{ fontSize: 11, maxWidth: 280, wordBreak: "break-all" }}>
                        {p.endpoint_url}
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {p.response_parser}
                      </td>
                      <td>{p.model_count}</td>
                    </tr>
                  ))}
                  {profiles.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="muted" style={{ padding: 12 }}>
                        No profiles.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}

          {selectedProfileId ? (
            <div className="card" style={{ padding: 14, display: "grid", gap: 12 }}>
              <div style={{ fontWeight: 700 }}>Edit profile</div>
              <label className="field">
                <span className="label">Profile id</span>
                <input className="input mono" readOnly value={selectedProfileId} />
              </label>
              <label className="field">
                <span className="label">Label</span>
                <input className="input" value={profileEditLabel} onChange={(e) => setProfileEditLabel(e.target.value)} />
              </label>
              <label className="field">
                <span className="label">Endpoint URL</span>
                <input className="input mono" value={profileEditEndpoint} onChange={(e) => setProfileEditEndpoint(e.target.value)} />
              </label>
              <label className="field">
                <span className="label">Response parser</span>
                <select className="select" value={profileEditParser} onChange={(e) => setProfileEditParser(e.target.value)}>
                  <option value="openai_chat">openai_chat</option>
                  <option value="openai_responses">openai_responses</option>
                </select>
              </label>
              <div className="field" style={{ marginBottom: 0 }}>
                <span className="label">Parameters</span>
                <ProfileParamsTable
                  rows={profileEditParamRows}
                  onChange={setProfileEditParamRows}
                  disabled={profileSaving}
                />
              </div>
              <div className="actions" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <button
                  type="button"
                  className="btn btnSm btnDanger"
                  disabled={
                    profileSaving || (profiles.find((x) => x.id === selectedProfileId)?.model_count ?? 0) > 0
                  }
                  title={
                    (profiles.find((x) => x.id === selectedProfileId)?.model_count ?? 0) > 0
                      ? "Reassign all models before deleting"
                      : undefined
                  }
                  onClick={() => {
                    const p = profiles.find((x) => x.id === selectedProfileId);
                    if (p) setProfileDeleteTarget(p);
                  }}
                >
                  Delete
                </button>
                <button type="button" className="btn btnSm" disabled={profileSaving} onClick={() => void saveProfileEdit()}>
                  Save
                </button>
              </div>
            </div>
          ) : null}

          {profileAddOpen ? (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 80,
                padding: 16
              }}
              role="presentation"
              onMouseDown={() => {
                setNewProfileParamRows([]);
                setProfileAddOpen(false);
              }}
            >
              <div
                className="card"
                style={{ maxWidth: 960, width: "100%", display: "grid", gap: 12, maxHeight: "90vh", overflowY: "auto" }}
                role="dialog"
                aria-modal="true"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div style={{ fontWeight: 700 }}>Add API profile</div>
                <label className="field">
                  <span className="label">Profile id</span>
                  <input className="input mono" value={newProfileId} onChange={(e) => setNewProfileId(e.target.value)} />
                </label>
                <label className="field">
                  <span className="label">Label</span>
                  <input className="input" value={newProfileLabel} onChange={(e) => setNewProfileLabel(e.target.value)} />
                </label>
                <label className="field">
                  <span className="label">Endpoint URL</span>
                  <input className="input mono" value={newProfileEndpoint} onChange={(e) => setNewProfileEndpoint(e.target.value)} />
                </label>
                <label className="field">
                  <span className="label">Response parser</span>
                  <select className="select" value={newProfileParser} onChange={(e) => setNewProfileParser(e.target.value)}>
                    <option value="openai_chat">openai_chat</option>
                    <option value="openai_responses">openai_responses</option>
                  </select>
                </label>
                <div className="field" style={{ marginBottom: 0 }}>
                  <span className="label">Parameters</span>
                  <ProfileParamsTable
                    rows={newProfileParamRows}
                    onChange={setNewProfileParamRows}
                    disabled={profileSaving}
                  />
                </div>
                <div className="actions" style={{ justifyContent: "flex-end" }}>
                  <a
                    href="#"
                    className="linkBtn menuLinkAction"
                    onClick={(e) => {
                      e.preventDefault();
                      setNewProfileParamRows([]);
                      setProfileAddOpen(false);
                    }}
                  >
                    Cancel
                  </a>
                  <a
                    href="#"
                    className="linkBtn menuLinkAction"
                    onClick={(e) => {
                      e.preventDefault();
                      void submitNewProfile();
                    }}
                  >
                    Create
                  </a>
                </div>
              </div>
            </div>
          ) : null}

          {profileDeleteTarget ? (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 81,
                padding: 16
              }}
              role="presentation"
              onMouseDown={() => !profileSaving && setProfileDeleteTarget(null)}
            >
              <div className="card" style={{ maxWidth: 400, width: "100%", display: "grid", gap: 12 }} onMouseDown={(e) => e.stopPropagation()}>
                <div style={{ fontWeight: 700 }}>Delete profile?</div>
                <p style={{ margin: 0, fontSize: 14 }}>
                  Remove <span className="mono">{profileDeleteTarget.id}</span>? This cannot be undone.
                </p>
                <div className="actions" style={{ justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" className="btn btnSm" disabled={profileSaving} onClick={() => setProfileDeleteTarget(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btnSm btnDanger" disabled={profileSaving} onClick={() => void confirmDeleteProfile()}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "openai_models" ? (
        <div className="tabPanel">
          <div className="card muted" style={{ marginBottom: 12, padding: 12, fontSize: 13 }}>
            Temporary tab: calls <span className="mono">GET https://api.openai.com/v1/models</span> using the hub’s{" "}
            <span className="mono">OPENAI_API_KEY</span>. Remove when no longer needed.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <button type="button" className="btn btnSm" disabled={openaiModelsLoading} onClick={() => void loadOpenaiModels()}>
              Refresh
            </button>
            {openaiFetchedAt ? (
              <span className="muted" style={{ fontSize: 12 }}>
                Last fetch: {openaiFetchedAt}
              </span>
            ) : null}
            {!openaiModelsLoading && openaiModels.length > 0 ? (
              <span className="muted" style={{ fontSize: 12 }}>
                {openaiModels.length} model{openaiModels.length === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
          {openaiModelsError ? <ErrorState detail={openaiModelsError} /> : null}
          {openaiModelsLoading ? <LoadingState label="Fetching OpenAI model list…" /> : null}
          {!openaiModelsLoading && !openaiModelsError && openaiModels.length === 0 ? (
            <div className="card muted" style={{ padding: 12 }}>
              No models returned.
            </div>
          ) : null}
          {!openaiModelsLoading && !openaiModelsError && openaiModels.length > 0 ? (
            <div className="card tableWrap">
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Model id</th>
                    <th>Owned by</th>
                    <th>Created (UTC)</th>
                  </tr>
                </thead>
                <tbody>
                  {openaiModels.map((row) => (
                    <tr key={row.id}>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {row.id}
                      </td>
                      <td>{row.owned_by ?? "—"}</td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {fmtOpenAiCreated(row.created)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {deleteTarget && canMutate ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 81,
            padding: 16
          }}
          role="presentation"
          onMouseDown={() => {
            if (savingModelId === deleteTarget.model_id) return;
            setDeleteTarget(null);
          }}
        >
          <div
            className="card"
            style={{ maxWidth: 440, width: "100%", display: "grid", gap: 14 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-model-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div id="delete-model-title" style={{ fontWeight: 700, fontSize: 16 }}>
              Delete model?
            </div>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
              You are about to remove this entry from the AI model registry. This cannot be undone.
            </p>
            <div
              style={{
                display: "grid",
                gap: 8,
                fontSize: 13,
                padding: "10px 12px",
                background: "var(--panel-2, rgba(0,0,0,0.04))",
                borderRadius: 8
              }}
            >
              <div>
                <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>
                  Model id
                </div>
                <div className="mono" style={{ fontWeight: 600 }}>
                  {deleteTarget.model_id}
                </div>
              </div>
              {deleteTarget.description ? (
                <div>
                  <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>
                    Description
                  </div>
                  <div>{deleteTarget.description}</div>
                </div>
              ) : null}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
                <div>
                  <span className="muted">Status</span> {deleteTarget.status}
                </div>
                <div>
                  <span className="muted">Tenant visible</span> {deleteTarget.tenant_visible ? "yes" : "no"}
                </div>
                <div>
                  <span className="muted">Hub default</span> {deleteTarget.is_hub_default ? "yes" : "no"}
                </div>
                <div>
                  <span className="muted">Default web</span> {deleteTarget.is_hub_default_web_model ? "yes" : "no"}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
                <div>
                  <span className="muted">30d calls</span> {deleteTarget.usage_30d_calls.toLocaleString()}
                </div>
                <div>
                  <span className="muted">30d est. cost</span> {fmtUsd(deleteTarget.usage_30d_cost)}
                </div>
              </div>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 12, lineHeight: 1.4 }}>
              If the model has usage in the log, the server will reject the delete; set status to disabled instead.
            </p>
            <div className="actions" style={{ justifyContent: "flex-end", gap: 12 }}>
              <a
                href="#"
                className="linkBtn menuLinkAction"
                aria-disabled={savingModelId === deleteTarget.model_id}
                onClick={(e) => {
                  e.preventDefault();
                  if (savingModelId === deleteTarget.model_id) return;
                  setDeleteTarget(null);
                }}
                style={
                  savingModelId === deleteTarget.model_id
                    ? { opacity: 0.45, pointerEvents: "none", textDecoration: "none" }
                    : undefined
                }
              >
                Cancel
              </a>
              <a
                href="#"
                className="linkBtn menuLinkAction linkBtnDanger"
                aria-disabled={savingModelId === deleteTarget.model_id}
                onClick={(e) => {
                  e.preventDefault();
                  if (savingModelId === deleteTarget.model_id) return;
                  void confirmDeleteModel();
                }}
                style={
                  savingModelId === deleteTarget.model_id
                    ? { opacity: 0.45, pointerEvents: "none", textDecoration: "none" }
                    : undefined
                }
              >
                {savingModelId === deleteTarget.model_id ? "Deleting…" : "Delete model"}
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}
